import React, { memo, useEffect, useMemo } from 'react';
import { useObservable } from '@core/utils/hooks/rxjs';
import { FormInput } from '@core/components/form';
import { Table } from 'reactstrap';
import { TextBlock } from '@modules/common/TextBlock';
import { FCGridFormData } from '@modules/settings/SettingsPage/Components/FormsTab/FormFields/FCGrid';
import { GridFormType } from './FormViewModal';

export interface FormGridViewProps {
  form: GridFormType;
  field: FCGridFormData;
}

export const FormGridView = memo((props: FormGridViewProps) => {
  const { form, field } = props;
  const cols = useMemo(() => field.no_cols, [field]);
  const rows = useMemo(() => field.no_rows, [field]);
  const items = useObservable(form.controls.rows.items$, []);

  useEffect(() => {
    const cols$ = Array.from({ length: cols }, _ => {
      return { title: '' };
    });
    const rows$ = Array.from({ length: rows }, _ => {
      return { cols: cols$ };
    });
    form.patchValue({ rows: rows$ });
  }, [form, cols, rows]);

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const clipboardData = e.clipboardData.getData('text');
    const rows = clipboardData.split('\n').filter(row => row.trim());

    const pastedData = rows.map(row => (row.includes('\t') ? row.split('\t') : row.split(/[,\s]+/)));

    const limitedData = pastedData.slice(0, field.no_rows).map(row => row.slice(0, field.no_cols));

    const newRows = Array.from({ length: field.no_rows }, (_, rowIndex) => ({
      cols: Array.from({ length: field.no_cols }, (_, colIndex) => ({
        title: limitedData[rowIndex]?.[colIndex] || '',
      })),
    }));

    form.patchValue({ rows: newRows });
  };

  const cleanMarkdownText = (text: string): string => {
    return (
      text
        // Remove bold formatting **text**
        .replace(/\*\*(.*?)\*\*/g, '$1')
        // Remove italic formatting *text*
        .replace(/\*(.*?)\*/g, '$1')
        // Remove code formatting `text`
        .replace(/`(.*?)`/g, '$1')
        // Remove links [text](url) -> text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        // Remove strikethrough ~~text~~
        .replace(/~~(.*?)~~/g, '$1')
        // Remove quotes from start and end
        .replace(/^["'](.*)["']$/, '$1')
        // Clean up extra spaces
        .replace(/\s+/g, ' ')
        .trim()
    );
  };

  const handleFormPaste = (e: React.ClipboardEvent, startRowIndex: number, startColIndex: number) => {
    e.preventDefault();
    const clipboardData = e.clipboardData.getData('text');
    const rows = clipboardData.split('\n').filter(row => row.trim());

    const pastedData = rows
      .map((row, index) => {
        if (row.includes('\t')) {
          return row.split('\t').map(cell => cleanMarkdownText(cell));
        } else if (row.includes('|')) {
          // Markdown table format
          const cells = row
            .split('|')
            .map(cell => cleanMarkdownText(cell))
            .filter(cell => cell.length > 0); // Remove empty cells from start/end pipes

          return cells;
        } else if (row.includes(',')) {
          // Comma-separated format (CSV)
          return row.split(',').map(cell => cleanMarkdownText(cell));
        } else {
          // Simple text content - keep as single cell
          return [cleanMarkdownText(row)]; // Keep entire content as one cell
        }
      })
      .filter(row => {
        // Filter out separator rows (like | ---- | ---- |) and empty rows
        const isValidRow =
          row.length > 0 &&
          !row.every(cell => /^-+$/.test(cell)) && // Separator rows
          !row.every(cell => cell.length === 0); // Empty rows

        return isValidRow;
      });

    // Get current form data
    const currentRows = items.map(row => row.controls.cols.items.map(col => col.controls.title.value || ''));

    // Create a copy of current data to modify
    const newData = currentRows.map(row => [...row]);

    // Apply pasted data starting from the clicked position
    pastedData.forEach((pastedRow, rowOffset) => {
      const targetRowIndex = startRowIndex + rowOffset;
      if (targetRowIndex >= field.no_rows) return; // Skip if outside grid bounds

      pastedRow.forEach((cellValue, colOffset) => {
        const targetColIndex = startColIndex + colOffset;
        if (targetColIndex >= field.no_cols) return; // Skip if outside grid bounds

        newData[targetRowIndex][targetColIndex] = cellValue;
      });
    });

    // Convert back to form structure
    const newRows = Array.from({ length: field.no_rows }, (_, rowIndex) => ({
      cols: Array.from({ length: field.no_cols }, (_, colIndex) => ({
        title: newData[rowIndex][colIndex] || '',
      })),
    }));

    form.patchValue({ rows: newRows });
  };

  return (
    <div>
      <TextBlock classNameTitle="font-weight-bold font-size-20" className="mb-1" title={field.title}>
        <div></div>
      </TextBlock>
      <Table bordered>
        {items.map((row, i) => (
          <tr key={i} className="mx-0">
            {row.controls.cols.items.map((ctrl, index) => (
              <td className="p-0" key={`${i + 1}-column-${index}`}>
                <FormInput
                  control={ctrl.controls.title}
                  style={{ border: 0, borderRadius: 0 }}
                  withoutGroup
                  onPaste={e => handleFormPaste(e, i, index)}
                />
              </td>
            ))}
          </tr>
        ))}
      </Table>
    </div>
  );
});
